import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getCompany, updateCompany } from '../../api/company';

const emptyCompany = {
  name: '',
  logo: '',
  address: '',
  phone: '',
  email: '',
  taxId: '',
  streetName: '',
  buildingNumber: '',
  additionalStreetName: '',
  plotIdentification: '',
  district: '',
  postalCode: '',
  city: '',
  stateProvince: '',
  country: 'Philippines',
  bankAccountNumber: '',
  bankDetails: ''
};

function buildAddress(form) {
  return [
    form.buildingNumber,
    form.streetName,
    form.additionalStreetName,
    form.plotIdentification,
    form.district,
    form.city,
    form.stateProvince,
    form.postalCode,
    form.country
  ].map((part) => String(part || '').trim()).filter(Boolean).join(', ');
}

export default function CompanySettings({ authConfig, onUnauthorized }) {
  const [form, setForm] = useState(emptyCompany);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('company');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCompany(authConfig);
      setForm({
        ...emptyCompany,
        name: data?.name ?? '',
        logo: data?.logo ?? '',
        address: data?.address ?? '',
        phone: data?.phone ?? '',
        email: data?.email ?? '',
        taxId: data?.taxId ?? '',
        streetName: data?.streetName ?? data?.address ?? '',
        buildingNumber: data?.buildingNumber ?? '',
        additionalStreetName: data?.additionalStreetName ?? '',
        plotIdentification: data?.plotIdentification ?? '',
        district: data?.district ?? '',
        postalCode: data?.postalCode ?? '',
        city: data?.city ?? '',
        stateProvince: data?.stateProvince ?? '',
        country: data?.country ?? 'Philippines',
        bankAccountNumber: data?.bankAccountNumber ?? '',
        bankDetails: data?.bankDetails ?? ''
      });
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to load company settings.');
    } finally {
      setLoading(false);
    }
  }, [authConfig, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file for the logo.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, logo: String(reader.result || '') }));
      setError('');
    };
    reader.onerror = () => setError('Failed to read logo file.');
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        ...form,
        address: buildAddress(form)
      };
      const saved = await updateCompany(payload, authConfig);
      setForm((prev) => ({ ...prev, ...saved }));
      setMessage('Company data saved.');
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'h-7 border border-[#5a5a5a] bg-[#303030] px-2 text-sm text-white outline-none focus:border-[#28aeea]';
  const labelClass = 'w-40 shrink-0 text-sm text-white';
  const rowClass = 'flex items-center gap-3';

  if (loading) {
    return (
      <div className="min-h-[520px] bg-[#2f2f2f] p-5 text-sm text-white">
        Loading company settings...
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="min-h-[calc(100vh-10rem)] bg-[#2f2f2f] text-white">
      <div className="border-b border-[#1da9e8] bg-[#242424]">
        <div className="flex h-9 items-stretch text-sm">
          <button
            type="button"
            onClick={() => setActiveTab('company')}
            className={`px-4 ${activeTab === 'company' ? 'bg-[#25a9e0] text-white' : 'hover:bg-[#333]'}`}
          >
            Company data
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('void')}
            className={`px-4 ${activeTab === 'void' ? 'bg-[#25a9e0] text-white' : 'hover:bg-[#333]'}`}
          >
            Void reasons
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('reset')}
            className={`px-4 ${activeTab === 'reset' ? 'bg-[#25a9e0] text-white' : 'hover:bg-[#333]'}`}
          >
            Reset database
          </button>
        </div>
      </div>

      <div className="flex gap-8 px-6 py-4">
        <button type="submit" disabled={saving} className="flex flex-col items-center gap-1 text-sm disabled:opacity-50">
          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 13l4 4L19 7" />
          </svg>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={() => setMessage('Fill company data, then press Save.')} className="flex flex-col items-center gap-1 text-sm">
          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8.25 9a3.75 3.75 0 117.15 1.58c-.97 1.02-2.15 1.62-2.15 3.17M12 18h.01" />
          </svg>
          Help
        </button>
      </div>

      {error && <div className="mx-6 mb-3 border border-red-500 bg-red-950/40 px-3 py-2 text-sm text-red-100">{error}</div>}
      {message && <div className="mx-6 mb-3 border border-green-500 bg-green-950/40 px-3 py-2 text-sm text-green-100">{message}</div>}

      {activeTab === 'company' && (
        <div className="px-6 pb-10">
          <div className="mb-3 flex items-center gap-2">
            <h4 className="text-base font-medium">My company data</h4>
            <div className="h-px flex-1 bg-[#5a5a5a]" />
          </div>

          <div className="max-w-4xl space-y-2">
            <div className={rowClass}>
              <label className={labelClass}>Name</label>
              <input name="name" value={form.name} onChange={handleChange} className={`${inputClass} w-[500px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>Tax number</label>
              <input name="taxId" value={form.taxId} onChange={handleChange} className={`${inputClass} w-[150px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>Street name</label>
              <textarea name="streetName" value={form.streetName} onChange={handleChange} rows={2} className="min-h-[58px] w-[500px] border border-[#5a5a5a] bg-[#303030] px-2 py-1 text-sm text-white outline-none focus:border-[#28aeea]" />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>Building number</label>
              <input name="buildingNumber" value={form.buildingNumber} onChange={handleChange} className={`${inputClass} w-[100px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>Additional street name</label>
              <input name="additionalStreetName" value={form.additionalStreetName} onChange={handleChange} className={`${inputClass} w-[500px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>Plot identification</label>
              <input name="plotIdentification" value={form.plotIdentification} onChange={handleChange} className={`${inputClass} w-[200px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>District</label>
              <input name="district" value={form.district} onChange={handleChange} className={`${inputClass} w-[300px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>Postal code</label>
              <input name="postalCode" value={form.postalCode} onChange={handleChange} className={`${inputClass} w-[100px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>City</label>
              <input name="city" value={form.city} onChange={handleChange} className={`${inputClass} w-[300px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>State / Province</label>
              <input name="stateProvince" value={form.stateProvince} onChange={handleChange} className={`${inputClass} w-[300px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>Country</label>
              <select name="country" value={form.country} onChange={handleChange} className={`${inputClass} w-[300px]`}>
                <option>Philippines</option>
                <option>United States</option>
                <option>Japan</option>
                <option>Singapore</option>
              </select>
            </div>
            <div className={rowClass}>
              <label className={labelClass}>Phone number</label>
              <input name="phone" value={form.phone} onChange={handleChange} className={`${inputClass} w-[300px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>Email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} className={`${inputClass} w-[300px] uppercase`} />
            </div>
          </div>

          <div className="mb-3 mt-5 flex items-center gap-2">
            <h4 className="text-base font-medium">Bank account</h4>
            <div className="h-px flex-1 bg-[#5a5a5a]" />
          </div>
          <div className="max-w-4xl space-y-2">
            <div className={rowClass}>
              <label className={labelClass}>Bank acc. number</label>
              <input name="bankAccountNumber" value={form.bankAccountNumber} onChange={handleChange} className={`${inputClass} w-[300px]`} />
            </div>
            <div className={rowClass}>
              <label className={labelClass}>Bank details</label>
              <textarea name="bankDetails" value={form.bankDetails} onChange={handleChange} rows={3} className="min-h-[62px] w-[500px] border border-[#5a5a5a] bg-[#303030] px-2 py-1 text-sm text-white outline-none focus:border-[#28aeea]" />
            </div>
          </div>

          <div className="mb-3 mt-5 flex items-center gap-2">
            <h4 className="text-base font-medium">Logo</h4>
            <div className="h-px flex-1 bg-[#5a5a5a]" />
          </div>
          <div className="space-y-4">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
            <div className="flex gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} className="w-32 border border-[#5a5a5a] bg-[#303030] px-4 py-2 text-sm hover:bg-[#3a3a3a]">Browse</button>
              <button type="button" onClick={() => setForm((prev) => ({ ...prev, logo: '' }))} className="w-32 border border-[#5a5a5a] bg-[#303030] px-4 py-2 text-sm hover:bg-[#3a3a3a]">Clear</button>
            </div>
            {form.logo ? (
              <img src={form.logo} alt="Company logo" className="max-h-20 max-w-[260px] object-contain" />
            ) : (
              <div className="text-sm text-gray-400">No logo selected.</div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'void' && (
        <div className="px-6 pb-10 text-sm text-gray-200">
          Void reasons are managed from the Void reasons menu. This tab is shown here to match the Aronium layout.
        </div>
      )}

      {activeTab === 'reset' && (
        <div className="px-6 pb-10 text-sm text-red-100">
          Database reset is intentionally not available here. This prevents accidental data loss.
        </div>
      )}
    </form>
  );
}
